import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ONBOARD } from "@/constants/testIds";
import { extractPatterns, FALLBACK_CATEGORIES } from "@/components/onboard/patterns";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = "natant.onboard.draft.v1";

function readDraft() {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(DRAFT_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}
function writeDraft(next) {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
	} catch {
		/* quota errors — silently ignore */
	}
}
function clearDraft() {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.removeItem(DRAFT_KEY);
	} catch {
		/* ignore */
	}
}

function relTime(ts) {
	if (!ts) return "";
	const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
	if (diff < 5) return "just now";
	if (diff < 60) return `${diff}s ago`;
	if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
	return `${Math.floor(diff / 3600)}h ago`;
}

const TOOLS = [
	"Salesforce",
	"HubSpot",
	"Shopify",
	"SAP",
	"NetSuite",
	"Zendesk",
	"Notion",
	"Slack",
	"Snowflake",
	"Segment",
	"Airtable",
	"Custom in-house",
];

const STEP_LABELS = ["Describe", "Refine", "Confirm"];

function StepDots({ step }) {
	return (
		<div className="flex items-center gap-3">
			{STEP_LABELS.map((label, i) => (
				<div
					key={label}
					data-testid={ONBOARD.stepDot}
					className="flex items-center gap-2"
				>
					<span
						className="w-2.5 h-2.5 rounded-full transition-colors"
						style={{ background: i <= step ? "var(--accent-green)" : "rgba(15,15,15,0.18)" }}
					/>
					<span
						className={`text-[12px] font-medium transition-colors ${i === step ? "text-[var(--bone)]" : "text-[var(--bone-dim)]"}`}
					>
						{label}
					</span>
					{i < STEP_LABELS.length - 1 && (
						<span className="w-8 h-px bg-[var(--border-line)] ml-1" />
					)}
				</div>
			))}
		</div>
	);
}

export default function OnboardPage() {
	const navigate = useNavigate();
	const inputRef = useRef(null);
	const [description, setDescription] = useState("");
	const [companyUrl, setCompanyUrl] = useState("");
	const [tools, setTools] = useState([]);
	const [analogy, setAnalogy] = useState("");
	const [state, setState] = useState("empty"); // empty | composing | processing | draft | thin | done
	const [patterns, setPatterns] = useState([]);
	const [thin, setThin] = useState(false);
	const [fallbackCategory, setFallbackCategory] = useState("");
	const [confirmations, setConfirmations] = useState({}); // {id: "accepted" | "dismissed"}
	const [slotEdits, setSlotEdits] = useState({}); // {`${id}:${label}`: value}
	const [org, setOrg] = useState("");
	const [email, setEmail] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [savedAt, setSavedAt] = useState(null);
	const [restoredNote, setRestoredNote] = useState(false);
	const [nowTick, setNowTick] = useState(0);
	const hydratedRef = useRef(false);

	// Hydrate from localStorage on mount (once)
	useEffect(() => {
		const d = readDraft();
		if (d) {
			setDescription(d.description || "");
			setCompanyUrl(d.companyUrl || "");
			setTools(Array.isArray(d.tools) ? d.tools : []);
			setAnalogy(d.analogy || "");
			setOrg(d.org || "");
			setEmail(d.email || "");
			setSavedAt(d.savedAt || null);
			const restoredPatterns = Array.isArray(d.patterns) ? d.patterns : [];
			if (restoredPatterns.length) setPatterns(restoredPatterns);
			if (d.confirmations && typeof d.confirmations === "object") setConfirmations(d.confirmations);
			if (d.slotEdits && typeof d.slotEdits === "object") setSlotEdits(d.slotEdits);
			if (typeof d.fallbackCategory === "string") setFallbackCategory(d.fallbackCategory);
			if (typeof d.thin === "boolean") setThin(d.thin);
			if (d.description) {
				if (d.panelState === "draft" && restoredPatterns.length) setState("draft");
				else if (d.panelState === "thin") setState("thin");
				else setState("composing");
				setRestoredNote(true);
			}
		}
		hydratedRef.current = true;
		inputRef.current?.focus();
	}, []);

	// Auto-save draft on relevant changes (debounced)
	useEffect(() => {
		if (!hydratedRef.current) return;
		if (state === "done") return;
		const t = setTimeout(() => {
			const ts = Date.now();
			const panelState = state === "draft" || state === "thin" ? state : "composing";
			writeDraft({
				description, companyUrl, tools, analogy, org, email,
				patterns, confirmations, slotEdits, fallbackCategory, thin,
				panelState, savedAt: ts,
			});
			setSavedAt(ts);
		}, 500);
		return () => clearTimeout(t);
	}, [description, companyUrl, tools, analogy, org, email, patterns, confirmations, slotEdits, fallbackCategory, thin, state]);

	// Tick relative time indicator every 15s
	useEffect(() => {
		const id = setInterval(() => setNowTick((n) => n + 1), 15000);
		return () => clearInterval(id);
	}, []);

	const step = useMemo(() => {
		if (state === "empty" || state === "composing") return 0;
		if (state === "processing" || state === "draft" || state === "thin") return 1;
		return 2;
	}, [state]);

	const handleDescriptionChange = (e) => {
		setDescription(e.target.value);
		if (state === "empty" && e.target.value.length > 0) setState("composing");
		if (state !== "empty" && state !== "composing") setState("composing");
	};

	const toggleTool = (t) => {
		setTools((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
	};

	const applyExtraction = (results, isThin) => {
		setPatterns(results);
		setThin(isThin);
		// Pre-accept high-confidence patterns
		const conf = {};
		results.forEach((r) => {
			if (r.confidence === "high") conf[r.id] = "accepted";
		});
		setConfirmations(conf);
		setState(isThin ? "thin" : "draft");
	};

	const runExtraction = async () => {
		if (!description.trim() || state === "processing") return;
		setState("processing");
		try {
			const { data } = await axios.post(`${API}/onboarding/extract`, {
				description: description.trim(),
				company_url: companyUrl.trim() || null,
				tools,
				analogy: analogy.trim() || null,
			});
			applyExtraction(Array.isArray(data.patterns) ? data.patterns : [], !!data.thin);
		} catch {
			// Live AI reader unavailable — fall back to the on-device keyword matcher
			const { results, thin: isThin } = extractPatterns({
				description,
				url: companyUrl,
				tools,
				analogy,
			});
			applyExtraction(results, isThin);
			toast.info("Live AI reader unavailable — used on-device match.");
		}
	};

	const acceptPattern = (id) => setConfirmations((c) => ({ ...c, [id]: "accepted" }));
	const dismissPattern = (id) => setConfirmations((c) => ({ ...c, [id]: "dismissed" }));

	const editSlot = (id, label, value) => {
		setSlotEdits((s) => ({ ...s, [`${id}:${label}`]: value }));
	};

	const slotValue = (id, label, original) => slotEdits[`${id}:${label}`] ?? original;

	const acceptedPatternIds = useMemo(
		() => Object.entries(confirmations).filter(([, v]) => v === "accepted").map(([k]) => k),
		[confirmations],
	);

	const canSubmit =
		description.trim().length > 0 &&
		org.trim().length > 0 &&
		email.trim().length > 0 &&
		(acceptedPatternIds.length > 0 || fallbackCategory.length > 0);

	const submit = async () => {
		if (!canSubmit || submitting) return;
		setSubmitting(true);
		try {
			await axios.post(`${API}/onboarding/describe`, {
				email: email.trim(),
				organisation: org.trim(),
				description: description.trim(),
				company_url: companyUrl.trim() || null,
				tools,
				analogy: analogy.trim() || null,
				accepted_patterns: acceptedPatternIds,
				fallback_category: fallbackCategory || null,
			});
			toast.success("Received. We reply within 5 business days.");
			clearDraft();
			setSavedAt(null);
			setState("done");
		} catch (err) {
			if (err?.response?.status === 409) {
				// One application per network — treat a duplicate as received
				toast.info("An application was already submitted from this network — we've got it.");
				clearDraft();
				setSavedAt(null);
				setState("done");
			} else {
				toast.error("Could not submit. Please try again.");
			}
		} finally {
			setSubmitting(false);
		}
	};

	const restart = () => {
		setDescription("");
		setCompanyUrl("");
		setTools([]);
		setAnalogy("");
		setPatterns([]);
		setThin(false);
		setFallbackCategory("");
		setConfirmations({});
		setSlotEdits({});
		setOrg("");
		setEmail("");
		clearDraft();
		setSavedAt(null);
		setState("empty");
		inputRef.current?.focus();
	};

	const clearDraftAndReset = () => {
		restart();
		toast.success("Draft cleared.");
	};

	const editAgain = () => setState("composing");

	return (
		<div
			data-testid={ONBOARD.page}
			className="min-h-screen bg-[var(--obsidian)] text-[var(--bone)]"
		>
			<Seo title="Apply for Cohort 1 — Natant" description="One paragraph in your own words is all it takes to apply to Cohort 1 — three founding companies that run their business on Natant." path="/apply" noindex />

			{/* Slim top bar (page-specific, not the marketing nav) */}
			<header className="border-b border-[var(--border-line)]">
				<div className="max-container flex items-center justify-between h-[64px]">
					<Link to="/" className="flex items-center gap-2.5">
						<span className="font-display text-[19px] tracking-[-0.02em] font-semibold whitespace-nowrap">Natant <span className="wordmark-ai">AI</span> Labs<span className="text-[var(--accent-green)]">.</span></span>
					</Link>
					<StepDots step={step} />
					<Link
						to="/"
						className="text-sm text-[var(--bone-dim)] hover:text-[var(--bone)] transition-colors font-medium"
					>
						Save & exit →
					</Link>
				</div>
			</header>

			<main className="max-container py-16 md:py-24">
				{/* Restored-from-draft banner */}
				<AnimatePresence>
					{restoredNote && state !== "done" && (
						<motion.div
							initial={{ opacity: 0, y: -8 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -8 }}
							transition={{ duration: 0.5 }}
							className="mb-8 flex items-center justify-between rounded-full border border-[var(--border-line)] bg-[var(--surface)] px-5 py-3"
						>
							<div className="flex items-center gap-3 text-sm text-[var(--bone)]">
								<span
									className="inline-flex w-6 h-6 items-center justify-center rounded-full"
									style={{ background: "var(--accent-green)", color: "var(--obsidian)" }}
								>
									↺
								</span>
								<span>
									Picked up where you left off — no account needed, saved locally to
									this browser.
								</span>
							</div>
							<div className="flex items-center gap-3">
								<button
									type="button"
									onClick={clearDraftAndReset}
									className="text-xs text-[var(--bone-dim)] hover:text-[var(--bone)] transition-colors font-medium underline underline-offset-4"
								>
									Start fresh
								</button>
								<button
									type="button"
									onClick={() => setRestoredNote(false)}
									className="text-[var(--bone-dim)] hover:text-[var(--bone)] transition-colors text-base leading-none"
									aria-label="Dismiss"
								>
									×
								</button>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
				<AnimatePresence mode="wait">
					{state !== "done" ? (
						<motion.div
							key="working"
							initial={{ opacity: 0, y: 12 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -12 }}
							transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
							className="grid grid-cols-12 gap-8 md:gap-14"
						>
							{/* Left: prompt + primary field + accelerants */}
							<section className="col-span-12 md:col-span-7">
								<div className="eyebrow mb-3">Cohort 1 · Describe your business</div>
								<h1 className="hm-h1 text-[9vw] md:text-[4.4vw] leading-[1.02] mb-6">
									<span className="block">Tell us what your</span>
									<span className="block">
										business <em className="em">actually</em> does.
									</span>
								</h1>
								<p className="text-[var(--bone-dim)] text-base md:text-lg leading-relaxed max-w-[52ch] mb-10">
									One paragraph in your own words. No categories to pick, no
									dropdowns to open. We read what you write and pre-fill the rest —
									you stay in control of every line before you confirm.
								</p>

								{/* Primary field */}
								<label className="block relative">
									<span className="eyebrow mb-2 block">Describe your business</span>
									<textarea
										ref={inputRef}
										value={description}
										onChange={handleDescriptionChange}
										data-testid={ONBOARD.primaryInput}
										rows={5}
										placeholder="You sell hand-poured candles to independent boutiques across North America. Wholesale ships from a Brooklyn studio; a small D2C site handles gifting orders."
										className="w-full bg-transparent text-[var(--bone)] border border-[var(--border-line)] rounded-2xl px-5 py-4 text-lg leading-snug outline-none focus:border-[var(--bone)] transition-colors resize-none"
										style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
									/>
									<div className="mt-3 flex items-center justify-between">
										<div className="flex items-center gap-3">
											<span className="text-xs text-[var(--bone-dim)]">
												{description.trim().split(/\s+/).filter(Boolean).length} words · plain sentences work best
											</span>
											{savedAt && description && (
												<span
													className="text-xs text-[var(--bone-dim)] flex items-center gap-1.5"
													key={nowTick}
												>
													<span
														className="inline-block w-1.5 h-1.5 rounded-full"
														style={{ background: "var(--accent-green)" }}
													/>
													Draft saved · {relTime(savedAt)}
												</span>
											)}
										</div>
										<button
											type="button"
											onClick={runExtraction}
											disabled={!description.trim() || state === "processing"}
											data-testid={ONBOARD.analyseButton}
											className="pill-btn pill-btn--solid disabled:opacity-50 disabled:cursor-not-allowed"
											style={{ padding: "10px 22px", fontSize: 13 }}
										>
											{state === "processing" ? "Reading…" : "Read my description →"}
										</button>
									</div>
								</label>

								{/* Optional accelerants — clearly secondary */}
								<div className="mt-14">
									<div className="eyebrow mb-4">Optional · sharpens the read</div>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										<label className="block">
											<span className="text-xs text-[var(--bone-dim)] font-medium">
												Company website
											</span>
											<input
												type="text"
												value={companyUrl}
												onChange={(e) => setCompanyUrl(e.target.value)}
												placeholder="yourcompany.com"
												data-testid={ONBOARD.urlInput}
												className="brut-input"
											/>
										</label>
										<label className="block">
											<span className="text-xs text-[var(--bone-dim)] font-medium">
												A close analogy (optional)
											</span>
											<input
												type="text"
												value={analogy}
												onChange={(e) => setAnalogy(e.target.value)}
												placeholder="e.g. like Warby Parker for hearing aids"
												data-testid={ONBOARD.analogyInput}
												className="brut-input"
											/>
										</label>
									</div>

									<div className="mt-8">
										<span className="text-xs text-[var(--bone-dim)] font-medium">
											Tools you already run
										</span>
										<div className="mt-3 flex flex-wrap gap-2">
											{TOOLS.map((t) => {
												const on = tools.includes(t);
												return (
													<button
														key={t}
														type="button"
														onClick={() => toggleTool(t)}
														data-testid={ONBOARD.toolChip}
														className="px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition-all"
														style={{
															background: on ? "var(--bone)" : "transparent",
															color: on ? "var(--obsidian)" : "var(--bone)",
															borderColor: on ? "var(--bone)" : "rgba(15,15,15,0.18)",
														}}
													>
														{t}
													</button>
												);
											})}
										</div>
									</div>
								</div>
							</section>

							{/* Right: live extraction panel */}
							<aside className="col-span-12 md:col-span-5">
								<div className="sticky top-24 rounded-2xl border border-[var(--border-line)] bg-[var(--surface)] p-6 md:p-8">
									<div className="flex items-center justify-between mb-6">
										<div className="eyebrow">§ What we understood</div>
										{state === "draft" && (
											<button
												type="button"
												onClick={editAgain}
												data-testid={ONBOARD.editAgain}
												className="text-xs text-[var(--bone-dim)] hover:text-[var(--bone)] transition-colors font-medium underline underline-offset-4"
											>
												Edit description
											</button>
										)}
									</div>

									{state === "empty" && (
										<EmptyPreview />
									)}

									{state === "composing" && (
										<ComposingPreview description={description} />
									)}

									{state === "processing" && (
										<ProcessingPreview />
									)}

									{state === "draft" && (
										<DraftPanel
											patterns={patterns}
											confirmations={confirmations}
											onAccept={acceptPattern}
											onDismiss={dismissPattern}
											slotValue={slotValue}
											editSlot={editSlot}
										/>
									)}

									{state === "thin" && (
										<ThinFallback
											description={description}
											category={fallbackCategory}
											setCategory={setFallbackCategory}
											onEdit={editAgain}
										/>
									)}

									{(state === "draft" || state === "thin") && (
										<CommitBlock
											org={org}
											setOrg={setOrg}
											email={email}
											setEmail={setEmail}
											submit={submit}
											submitting={submitting}
											canSubmit={canSubmit}
										/>
									)}
								</div>
							</aside>
						</motion.div>
					) : (
						<motion.div
							key="done"
							initial={{ opacity: 0, y: 12 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
							className="grid grid-cols-12 gap-8"
							data-testid={ONBOARD.successState}
						>
							<div className="col-span-12 md:col-span-8 md:col-start-3 text-center py-24">
								<div
									className="w-14 h-14 rounded-full mx-auto mb-8 flex items-center justify-center"
									style={{ background: "var(--accent-green)", color: "var(--obsidian)" }}
								>
									<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
										<path d="M4 12 L10 18 L20 6" />
									</svg>
								</div>
								<h2 className="hm-h1 text-4xl md:text-6xl mb-6">
									<em className="em">Received.</em>
								</h2>
								<p className="text-[var(--bone-dim)] text-lg leading-relaxed max-w-[48ch] mx-auto mb-10">
									A partner at Natant will read your description in full and reply within 5 business days.
									Nothing you told us will be re-asked.
								</p>
								<div className="flex flex-wrap items-center justify-center gap-3">
									<Link to="/" className="pill-btn pill-btn--solid">
										Return home
									</Link>
									<button
										type="button"
										onClick={restart}
										data-testid={ONBOARD.restartButton}
										className="pill-btn"
									>
										Start a new one
									</button>
								</div>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</main>
		</div>
	);
}

/* ---------- sub-panels ---------- */

function EmptyPreview() {
	return (
		<div className="space-y-4">
			<div className="text-sm text-[var(--bone)] leading-relaxed">
				Start typing on the left. As soon as we have a sentence, we&rsquo;ll show
				you what we heard.
			</div>
			<div className="mt-6 space-y-3">
				{["Customer type", "Core offer", "Pricing model", "Primary channel"].map((s) => (
					<div key={s} className="flex items-baseline justify-between border-b border-dashed border-[var(--border-line)] pb-2">
						<span className="text-xs text-[var(--bone-dim)] font-medium">{s}</span>
						<span className="text-sm text-[var(--bone-dim)] italic">—</span>
					</div>
				))}
			</div>
		</div>
	);
}

function ComposingPreview({ description }) {
	const words = description.trim().split(/\s+/).filter(Boolean).length;
	return (
		<div className="space-y-4">
			<div className="text-sm text-[var(--bone-dim)]">Listening…</div>
			<div className="text-sm text-[var(--bone)] leading-relaxed border-l-2 border-[var(--accent-green)] pl-3 italic font-editorial">
				{description ? `"${description.slice(0, 240)}${description.length > 240 ? "…" : ""}"` : "—"}
			</div>
			<div className="text-xs text-[var(--bone-dim)]">
				{words} words captured · click <b>Read my description</b> when ready.
			</div>
		</div>
	);
}

function ProcessingPreview() {
	return (
		<div className="space-y-4" data-testid={ONBOARD.processing}>
			<div className="text-sm text-[var(--bone-dim)]">Reading your description…</div>
			<div className="space-y-3">
				{[0, 1, 2, 3].map((i) => (
					<motion.div
						key={i}
						initial={{ opacity: 0.3 }}
						animate={{ opacity: [0.3, 0.85, 0.3] }}
						transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.12 }}
						className="h-3 rounded-full bg-[var(--surface-2)]"
						style={{ width: `${70 + i * 6}%` }}
					/>
				))}
			</div>
			<div className="mt-4 flex items-center gap-2 text-xs text-[var(--bone-dim)]">
				<span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-green)] blink" />
				<span>Natant AI is reading your description · usually a few seconds</span>
			</div>
		</div>
	);
}

function ConfidenceBadge({ level }) {
	const map = {
		high: { label: "High confidence · pre-selected", bg: "var(--accent-green)", color: "var(--obsidian)" },
		medium: { label: "Suggested", bg: "transparent", color: "var(--bone)", border: true },
		low: { label: "Low signal", bg: "transparent", color: "var(--bone-dim)", border: true },
	};
	const m = map[level] || map.medium;
	return (
		<span
			className="text-[11px] px-2.5 py-1 rounded-full font-medium"
			style={{
				background: m.bg,
				color: m.color,
				border: m.border ? "1px solid rgba(15,15,15,0.2)" : "none",
			}}
		>
			{m.label}
		</span>
	);
}

function DraftPanel({ patterns, confirmations, onAccept, onDismiss, slotValue, editSlot }) {
	return (
		<div className="space-y-6">
			<div className="text-sm text-[var(--bone)] leading-relaxed">
				Here&rsquo;s the shape of your business. Edit any line — or dismiss the
				ones that don&rsquo;t fit.
			</div>
			{patterns.map((p) => {
				const status = confirmations[p.id];
				const dismissed = status === "dismissed";
				const accepted = status === "accepted";
				return (
					<motion.div
						key={p.id}
						layout
						data-testid={ONBOARD.patternCard}
						className="rounded-xl border p-5 transition-all"
						style={{
							background: accepted ? "var(--obsidian)" : "transparent",
							borderColor: accepted ? "var(--accent-green)" : dismissed ? "rgba(15,15,15,0.1)" : "rgba(15,15,15,0.18)",
							opacity: dismissed ? 0.45 : 1,
							borderStyle: p.confidence === "medium" && !accepted ? "dashed" : "solid",
						}}
					>
						<div className="flex items-start justify-between gap-3 mb-4">
							<div>
								<div className="text-xs text-[var(--bone-dim)] mb-1">Pattern</div>
								<div className="font-display text-xl font-semibold">{p.name}</div>
							</div>
							<ConfidenceBadge level={p.confidence} />
						</div>

						<div className="space-y-2.5">
							{p.slots.map((slot) => (
								<div
									key={slot.label}
									data-testid={ONBOARD.patternSlot}
									className="flex items-baseline justify-between gap-3 border-b border-dashed border-[var(--border-line)] pb-2"
								>
									<span className="text-xs text-[var(--bone-dim)] font-medium shrink-0">
										{slot.label}
									</span>
									<input
										type="text"
										value={slotValue(p.id, slot.label, slot.value)}
										onChange={(e) => editSlot(p.id, slot.label, e.target.value)}
										disabled={dismissed}
										className="flex-1 bg-transparent text-sm text-right text-[var(--bone)] outline-none"
										style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
									/>
								</div>
							))}
						</div>

						<div className="mt-4 flex items-center justify-end gap-2">
							{!dismissed && (
								<button
									type="button"
									onClick={() => onDismiss(p.id)}
									data-testid={ONBOARD.patternDismiss}
									className="text-xs text-[var(--bone-dim)] hover:text-[var(--bone)] transition-colors font-medium px-3 py-1.5"
								>
									Not us
								</button>
							)}
							{dismissed ? (
								<button
									type="button"
									onClick={() => onAccept(p.id)}
									className="text-xs text-[var(--bone-dim)] hover:text-[var(--bone)] transition-colors font-medium px-3 py-1.5"
								>
									Bring back
								</button>
							) : accepted ? (
								<span className="text-xs font-medium px-3 py-1.5" style={{ color: "var(--accent-green)" }}>
									✓ Confirmed
								</span>
							) : (
								<button
									type="button"
									onClick={() => onAccept(p.id)}
									data-testid={ONBOARD.patternAccept}
									className="pill-btn pill-btn--solid"
									style={{ padding: "8px 16px", fontSize: 12 }}
								>
									Confirm
								</button>
							)}
						</div>
					</motion.div>
				);
			})}
		</div>
	);
}

function ThinFallback({ description, category, setCategory, onEdit }) {
	return (
		<div className="space-y-6">
			<div className="text-sm text-[var(--bone)] leading-relaxed">
				Your description is short and we don&rsquo;t want to guess. Pick the
				closest category and we&rsquo;ll take it from there — <b>your text stays as-is</b>.
			</div>
			<div className="rounded-xl border border-[var(--border-line)] bg-[var(--obsidian)] p-4">
				<div className="text-xs text-[var(--bone-dim)] mb-2 font-medium">Your description</div>
				<div className="text-sm text-[var(--bone)] italic font-editorial leading-relaxed">
					&ldquo;{description || "—"}&rdquo;
				</div>
				<button
					type="button"
					onClick={onEdit}
					className="mt-3 text-xs text-[var(--bone-dim)] hover:text-[var(--bone)] transition-colors font-medium underline underline-offset-4"
				>
					Add more detail
				</button>
			</div>
			<label className="block">
				<span className="text-xs text-[var(--bone-dim)] font-medium">Closest category</span>
				<select
					value={category}
					onChange={(e) => setCategory(e.target.value)}
					data-testid={ONBOARD.fallbackSelect}
					className="brut-input"
				>
					<option value="">Pick one…</option>
					{FALLBACK_CATEGORIES.map((c) => (
						<option key={c} value={c}>
							{c}
						</option>
					))}
				</select>
			</label>
		</div>
	);
}

function CommitBlock({ org, setOrg, email, setEmail, submit, submitting, canSubmit }) {
	return (
		<div className="mt-8 pt-6 border-t border-[var(--border-line)]">
			<div className="eyebrow mb-4">Send to a Natant partner</div>
			<div className="space-y-4">
				<label className="block">
					<span className="text-xs text-[var(--bone-dim)] font-medium">Organisation</span>
					<input
						type="text"
						value={org}
						onChange={(e) => setOrg(e.target.value)}
						placeholder="e.g. Aurora Group"
						data-testid={ONBOARD.orgInput}
						className="brut-input"
					/>
				</label>
				<label className="block">
					<span className="text-xs text-[var(--bone-dim)] font-medium">Work email</span>
					<input
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="you@company.com"
						data-testid={ONBOARD.emailInput}
						className="brut-input"
					/>
				</label>
			</div>
			<div className="mt-6 flex items-center justify-between">
				<span className="text-xs text-[var(--bone-dim)]">
					You&rsquo;ll get a reply within 5 business days.
				</span>
				<button
					type="button"
					onClick={submit}
					disabled={!canSubmit || submitting}
					data-testid={ONBOARD.submitButton}
					className="pill-btn pill-btn--solid disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{submitting ? "Sending…" : "Send to Natant →"}
				</button>
			</div>
		</div>
	);
}
